import { useEffect, type VFC } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { CardGridArea } from "widgets";

import { renderGenomeAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.js";
import type { User } from "@/graphql.js";
import { type IGenomeState, shouldLoadGenome } from "@/redux/modules/genome.js";
import type { IAppState } from "@/redux/reducer.js";
import { SEQUENCING_TYPE, UNKNOWN_GENOME_NAME } from "@/settings/settings.js";
import { SampleParentsCard } from "@/ui-modules/entities/cards/SampleParentsCard.js";
import { VirtualGenomesCard } from "@/ui-modules/entities/cards/VirtualGenomesCard.tsx";
import { SampleCard } from "@/ui-modules/entities/samples/components/SampleCard.js";

import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import { EventStatusCard } from "../playbooks/events/EventStatusCard.js";
import { DatasetsCard } from "./cards/DatasetsCard.js";
import { GenomeCard } from "./cards/GenomeCard.js";
import { GenomicAnnotationCard } from "./cards/GenomicAnnotationCard.js";
import { GenomicNucleotideContentCard } from "./cards/GenomicNucleotideContentCard.js";
import { GenomicProteinContentCard } from "./cards/GenomicProteinContentCard.js";
import { GenomeOrganismPropertiesCard } from "./cards/OrganismPropertiesCard.js";
import { ReferencesCard } from "./cards/ReferencesCard.js";
import { RelatedProjectsCard } from "./cards/RelatedProjectsCard.js";
import { ResequencingDetailsCard } from "./cards/ResequencingDetailsCard.js";
import GenericEntitySummaryView from "./GenericEntitySummaryView.js";

/**
 * Note, here genomeId is a string since it is extracted by the router from the URL
 */

interface IGenomeSummaryViewOwnProps {
  title?: string;
  headerTags?: JSX.Element[];
}

/*
 * The view inside of GenomeDetailPage. The children are cards for the current genome.
 */
const GenomeSummaryView: VFC<IGenomeSummaryViewOwnProps> = (props) => {
  const { genomeId } = useParams<"genomeId">();
  const { isLoading, genome, relatedProjects, isLoadingRelatedProjects } = useSelector<IAppState, IGenomeState>(
    (state) => state.genome,
  );
  const currentUser = useSelector<IAppState, User>((state) => state.auth.session);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadGenome, genomeId);

  useEffect(() => {
    void load();
  }, [load]);

  const showSpinner = isLoading || !genome;

  return (
    <GenericEntitySummaryView
      title={(props.title ? `${props.title}: ` : "") + (genome ? genome.name : "")}
      subtitle={genome?.description ?? "Genome Summary"}
      entity={isLoading ? null : genome}
      headerTags={props.headerTags}
      headerAttributes={[
        <AttributesListWidget
          attributes={renderGenomeAttributesWidgets(genome)}
          key={"genome-summary-view-widget-1"}
        />,
      ]}
      isLoading={showSpinner}
      getEntityName={() => genome?.name}
    >
      {genome && (
        <>
          <GenomeCard genome={genome} area={CardGridArea.Left} isEntitySummary={true} />
          <VirtualGenomesCard genomeId={genome.id} area={CardGridArea.Left} />
          <SampleCard id={genome.sample?.id} hidden={genome.sample?.hidden} title={"Associated Sample Info"} />
          <SampleParentsCard parent={genome.sample?.primaryParent} title={"Associated Parent Info"} />
          <ReferencesCard references={genome.allExternalReferences} area={CardGridArea.Left} />
          <DatasetsCard datasets={genome.datasets} area={CardGridArea.Left} />
          <GenomeOrganismPropertiesCard genome={genome} area={CardGridArea.Left} />

          {genome.sequencingType === SEQUENCING_TYPE.RE_SEQUENCED_VCF && (
            <ResequencingDetailsCard genome={genome} area={CardGridArea.Right} />
          )}
          {genome.id && genome?.name !== UNKNOWN_GENOME_NAME && (
            <GenomicAnnotationCard genomeId={genome.id} sequenced={genome.sequenced} area={CardGridArea.Right} />
          )}
          {genome.id && genome.name !== UNKNOWN_GENOME_NAME && (
            <GenomicProteinContentCard genomeId={genome.id} area={CardGridArea.Right} />
          )}
          {genome.id && genome.name !== UNKNOWN_GENOME_NAME && (
            <GenomicNucleotideContentCard genomeId={genome.id} area={CardGridArea.Right} />
          )}
          <ReferencesCard
            title="Literature References"
            references={genome.literatureReferences}
            area={CardGridArea.Right}
          />
          <EventStatusCard organismId={genome.id} area={CardGridArea.Panel} />
          <RelatedProjectsCard
            isLoading={isLoadingRelatedProjects}
            relatedProjects={relatedProjects}
            currentUser={currentUser}
            area={CardGridArea.Panel}
          />
        </>
      )}
    </GenericEntitySummaryView>
  );
};

export { GenomeSummaryView };
